'use client';

/**
 * WishlistSection — Section "Entrar na fila" da home / (Figma 81:70).
 *
 * Comportamento (per spec PL 2026-05-15):
 *   - Background full-width sage (#ACB7A8 do Figma)
 *   - Layout: headline + body (left) + Input pill com leadingIcon Mail + Button (right)
 *   - Validação client-side de email (regex) com mensagem de erro inline nativa do Input
 *   - Submit via Server Action (mesmo contrato do WaitlistForm legacy)
 *   - Feedback success/duplicate exibido abaixo do form com transition
 *
 * Honra catálogo:
 *   - Input componente facelift com prop `error` nativa (não inventar feedback inline)
 *   - Button variant="primary-medium" size="md"
 *   - SectionTitle pattern não cabe aqui (layout 2 cols com form ao invés de trailing)
 */
import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Loader2 } from 'lucide-react';

import { Button } from '@brisa/ui/components/ui/button';
import { Input } from '@brisa/ui/components/ui/input';
import { gaEvent } from '@brisa/shared/observability/ga';
import { clarityEvent } from '@brisa/shared/observability/clarity';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface WishlistSectionCopy {
  headline: string;
  subhead: string;
  emailLabel: string;
  emailPlaceholder?: string;
  submit: string;
  success: string;
  duplicate: string;
  invalid: string;
  errorGeneric: string;
}

export interface WishlistSectionProps {
  copy: WishlistSectionCopy;
  action: (formData: FormData) => Promise<{
    ok: boolean;
    code?: string;
    _track?: { event: string; props: Record<string, unknown> };
  }>;
  surface?: string;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'duplicate' | 'error';

export function WishlistSection({
  copy,
  action,
  surface = 'home_waitlist',
}: WishlistSectionProps) {
  const [status, setStatus] = React.useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setStatus('error');
      setErrorMsg(copy.invalid);
      return;
    }
    setStatus('submitting');
    setErrorMsg(null);
    const fd = new FormData();
    fd.append('email', email.trim());
    const result = await action(fd);
    if (result.ok) {
      setStatus('success');
      if (result._track) {
        gaEvent(result._track.event, result._track.props);
        clarityEvent(result._track.event, result._track.props);
      }
    } else if (result.code === 'EMAIL_EXISTS') {
      setStatus('duplicate');
    } else if (result.code === 'INVALID_EMAIL') {
      setStatus('error');
      setErrorMsg(copy.invalid);
    } else {
      setStatus('error');
      setErrorMsg(copy.errorGeneric);
    }
  };

  const isLocked = status === 'submitting' || status === 'success';

  return (
    <section
      id="waitlist"
      className="relative w-full bg-[#ACB7A8] py-16 lg:py-20"
      aria-labelledby="waitlist-headline"
      data-slot="wishlist-section"
    >
      <div className="mx-auto grid w-full max-w-[1024px] grid-cols-1 gap-10 px-6 sm:px-10 md:grid-cols-2 md:items-center">
        {/* Left — copy */}
        <div className="flex flex-col items-start gap-4">
          <h2
            id="waitlist-headline"
            className="font-body text-2xl font-medium leading-snug text-[hsl(var(--text-100))] md:text-[28px]"
          >
            {copy.headline}
          </h2>
          <p className="max-w-[44ch] font-body text-sm leading-relaxed text-[hsl(var(--text-56))]">
            {copy.subhead}
          </p>
        </div>

        {/* Right — form (Input pill + Button primary-medium) */}
        <form
          onSubmit={handleSubmit}
          noValidate
          className="flex w-full flex-col gap-3"
          data-surface={surface}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="flex-1">
              <label htmlFor="waitlist-email" className="sr-only">
                {copy.emailLabel}
              </label>
              <Input
                id="waitlist-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status === 'error') {
                    setStatus('idle');
                    setErrorMsg(null);
                  }
                }}
                placeholder={copy.emailPlaceholder ?? copy.emailLabel}
                autoComplete="email"
                inputMode="email"
                required
                disabled={isLocked}
                leadingIcon={<Mail size={16} aria-hidden="true" />}
                error={status === 'error' && errorMsg ? errorMsg : undefined}
              />
            </div>
            <Button
              type="submit"
              variant="primary-medium"
              size="md"
              disabled={isLocked}
              className="sm:shrink-0"
              data-event="waitlist-submitted"
              data-surface={surface}
            >
              {status === 'submitting' ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : null}
              {copy.submit}
            </Button>
          </div>

          {/* Feedback success/duplicate (error já é renderizado pelo Input nativo) */}
          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <motion.p
                key="success"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                role="status"
                className="font-body text-sm font-medium leading-tight text-[hsl(var(--text-100))]"
              >
                {copy.success}
              </motion.p>
            ) : null}
            {status === 'duplicate' ? (
              <motion.p
                key="duplicate"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                role="status"
                className="font-body text-sm font-medium leading-tight text-[hsl(var(--text-100))]"
              >
                {copy.duplicate}
              </motion.p>
            ) : null}
          </AnimatePresence>
        </form>
      </div>
    </section>
  );
}
